/**
 * Interactive 3D Block-Model Viewer  (Sprint P37h)
 *
 * Loads the project's block-model PLY point cloud (via
 * `/api/projects/{pid}/block-model/export.ply?colour_by=grade`) into a
 * React-Three-Fiber scene with rotate / zoom / pan controls and toggles
 * for grade / classification / ore-vs-waste colour modes.
 *
 * No server-side rendering — the viewer runs entirely in the browser
 * using WebGL. Suitable for review sessions and interactive geology QC.
 */
/* eslint-disable react/no-unknown-property */  /* R3F lowercased three.js primitives */
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, GizmoHelper, GizmoViewport, Grid } from "@react-three/drei";
import * as THREE from "three";
import { PLYLoader } from "three/examples/jsm/loaders/PLYLoader.js";
import { toast } from "sonner";
import {
  Cube, Palette, ArrowsClockwise, ArrowClockwise, MagnifyingGlass, X,
  Camera, Images, Trash, CheckCircle,
} from "@phosphor-icons/react";

import { Button } from "@/components/ui/button";
import { api, API_BASE } from "@/lib/api";


// ─────────────────────────────────────────────────────────────────
// Point-cloud loader — pulls the PLY export from the backend
// ─────────────────────────────────────────────────────────────────
function useBlockPointCloud(projectId, colourBy, cutoff, maxPoints, refreshTick) {
  const [status, setStatus] = useState("idle");   // idle | loading | ready | error
  const [geometry, setGeometry] = useState(null);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setStatus("loading");
    const backend = process.env.REACT_APP_BACKEND_URL;
    const url = `${backend}/api/projects/${projectId}/block-model/export.ply?colour_by=${colourBy}&max_points=${maxPoints}${cutoff != null ? `&cutoff=${cutoff}` : ""}`;
    const loader = new PLYLoader();
    fetch(url, { credentials: "include" })
      .then((r) => {
        if (!r.ok) throw new Error(`PLY fetch failed — HTTP ${r.status}`);
        return r.arrayBuffer();
      })
      .then((buf) => {
        if (cancelled) return;
        const geom = loader.parse(buf);
        geom.computeBoundingBox();
        // Centre the geometry on origin so OrbitControls looks nice
        const box = geom.boundingBox;
        const centre = new THREE.Vector3();
        box.getCenter(centre);
        geom.translate(-centre.x, -centre.y, -centre.z);
        // Rescale into a ~200-unit cube for a stable camera framing
        const size = new THREE.Vector3();
        box.getSize(size);
        const scale = 200.0 / Math.max(size.x, size.y, size.z, 1);
        geom.scale(scale, scale, scale);
        setGeometry(geom);
        setMeta({
          nPoints: geom.attributes.position.count,
          bbox: [size.x, size.y, size.z],
          scale,
        });
        setStatus("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setStatus("error");
        toast.error(`3D viewer — ${err.message}`);
      });
    return () => { cancelled = true; };
  }, [projectId, colourBy, cutoff, maxPoints, refreshTick]);

  return { status, geometry, meta };
}


// ─────────────────────────────────────────────────────────────────
// Scene inner (needs `useThree` — so must live inside <Canvas>)
// ─────────────────────────────────────────────────────────────────
function BlockPointCloud({ geometry, pointSize }) {
  const ref = useRef();
  const material = useMemo(() => new THREE.PointsMaterial({
    size: pointSize,
    vertexColors: true,
    sizeAttenuation: true,
    transparent: true,
    opacity: 0.95,
    depthWrite: false,
  }), [pointSize]);

  useEffect(() => {
    if (ref.current) ref.current.material = material;
  }, [material]);

  if (!geometry) return null;
  return <points ref={ref} geometry={geometry} material={material} />;
}


function ResetCameraButton({ resetKey, geometry }) {
  const { camera, controls } = useThree();
  useEffect(() => {
    if (!geometry) {
      camera.position.set(180, 130, 180);
      camera.lookAt(0, 0, 0);
      camera.updateProjectionMatrix();
      return;
    }
    // Auto-frame based on the actual (rescaled) bounding box
    geometry.computeBoundingBox();
    const box = geometry.boundingBox;
    const size = new THREE.Vector3();
    box.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z, 1);
    const dist = maxDim * 1.6;   // pull back far enough to see everything
    camera.position.set(dist, dist * 0.7, dist);
    camera.lookAt(0, 0, 0);
    camera.near = Math.max(0.1, maxDim * 0.001);
    camera.far  = Math.max(2000, maxDim * 20);
    camera.updateProjectionMatrix();
    if (controls) {
      controls.target.set(0, 0, 0);
      controls.update?.();
    }
  }, [camera, controls, geometry, resetKey]);
  return null;
}


// ─────────────────────────────────────────────────────────────────
// CameraStateProbe — keeps the parent's ref updated with the current
// camera pose + target every frame, so `handleCapture` can read them
// synchronously without hooking into R3F state each time.
// ─────────────────────────────────────────────────────────────────
function CameraStateProbe({ stateRef }) {
  const { camera, controls } = useThree();
  useFrame(() => {
    const target = controls?.target ?? new THREE.Vector3(0, 0, 0);
    stateRef.current = {
      camera_pos:    [camera.position.x, camera.position.y, camera.position.z],
      camera_target: [target.x, target.y, target.z],
      fov:           camera.fov,
    };
  });
  return null;
}


// ─────────────────────────────────────────────────────────────────
// Public component
// ─────────────────────────────────────────────────────────────────
export default function BlockModelViewer({ projectId, cutoff, onClose }) {
  const [colourBy, setColourBy] = useState("grade");     // grade | classification | ore_waste
  const [pointSize, setPointSize] = useState(2.4);
  const [maxPoints, setMaxPoints] = useState(60_000);
  const [resetKey, setResetKey] = useState(0);
  const [refreshTick, setRefreshTick] = useState(0);
  const [captureBusy, setCaptureBusy] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);

  // Refs that survive re-renders — set inside the R3F scene
  const glRef = useRef(null);
  const cameraStateRef = useRef(null);

  const { status, geometry, meta } = useBlockPointCloud(
    projectId, colourBy, cutoff, maxPoints, refreshTick,
  );

  // ── Gallery: fetch on demand ──────────────────────────────────
  const reloadGallery = useCallback(async () => {
    if (!projectId) return;
    setGalleryLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/block-model/captured-views`);
      setGallery(Array.isArray(data?.views) ? data.views : []);
    } catch (err) {
      toast.error(`Failed to load captured views · ${err?.message || "?"}`);
    } finally {
      setGalleryLoading(false);
    }
  }, [projectId]);

  useEffect(() => { reloadGallery(); }, [reloadGallery]);

  // ── Capture the current WebGL canvas + camera pose ──────────
  const handleCapture = useCallback(async () => {
    if (captureBusy) return;
    const gl = glRef.current;
    const cam = cameraStateRef.current;
    if (!gl || !gl.domElement) {
      toast.error("3D canvas not ready yet");
      return;
    }
    if (!cam) {
      toast.error("Camera state not yet initialised — orbit once and retry");
      return;
    }
    if (status !== "ready") {
      toast.error("Block model still loading — please wait");
      return;
    }
    setCaptureBusy(true);
    try {
      const canvas = gl.domElement;
      // Reject blatantly empty canvases
      if (canvas.width < 200 || canvas.height < 150) {
        toast.error("Canvas is too small to capture");
        setCaptureBusy(false);
        return;
      }
      // With `preserveDrawingBuffer: true` (Canvas gl config) the WebGL
      // framebuffer holds the last rendered frame — toBlob works safely.
      const blob = await new Promise((resolve) =>
        canvas.toBlob((b) => resolve(b), "image/png", 0.95));
      if (!blob) throw new Error("toBlob returned null");
      if (blob.size < 1024) {
        throw new Error("Captured image is empty — try rotating the view first");
      }

      const payload = {
        camera_pos:    cam.camera_pos,
        camera_target: cam.camera_target,
        fov:           cam.fov,
        colour_mode:   colourBy,
        point_size:    pointSize,
        max_points:    maxPoints,
        cutoff:        cutoff ?? null,
        n_points:      meta?.nPoints || 0,
        model_id:      projectId,
        visible_layers: ["blocks", "grid", "gizmo"],
        clipping:      {},
        caption:       `${(colourBy || "").replace("_", "/")} view · ${new Date().toISOString().slice(0, 16).replace("T", " ")}`,
        review_session: window.sessionStorage?.getItem("sres.review_session") || "",
        captured_by:   window.localStorage?.getItem("sres.username") || "",
      };

      const fd = new FormData();
      fd.append("payload_json", JSON.stringify(payload));
      fd.append("file", blob, `capture_${Date.now()}.png`);

      const { data } = await api.post(
        `/projects/${projectId}/block-model/captured-view`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });

      toast.success(`Captured view saved · will appear in Chapter 19 · ${data?.id?.slice(0, 8)}`,
                     { duration: 3500 });
      await reloadGallery();
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || err?.message || "Unknown error";
      toast.error(`Capture failed · ${msg}`);
    } finally {
      setCaptureBusy(false);
    }
  }, [captureBusy, status, colourBy, pointSize, maxPoints, cutoff,
      meta, projectId, reloadGallery]);

  const handleDelete = useCallback(async (vid) => {
    if (!vid) return;
    try {
      await api.delete(`/projects/${projectId}/block-model/captured-view/${vid}`);
      toast.success("Captured view removed");
      await reloadGallery();
    } catch (err) {
      toast.error(`Delete failed · ${err?.message || "?"}`);
    }
  }, [projectId, reloadGallery]);

  const handleEditCaption = useCallback(async (vid, currentCaption) => {
    const next = window.prompt("Edit caption for this captured view", currentCaption || "");
    if (next == null) return;
    try {
      const fd = new FormData();
      fd.append("caption", next);
      await api.patch(`/projects/${projectId}/block-model/captured-view/${vid}`, fd);
      toast.success("Caption updated");
      await reloadGallery();
    } catch (err) {
      toast.error(`Update failed · ${err?.message || "?"}`);
    }
  }, [projectId, reloadGallery]);

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/85 backdrop-blur-md flex flex-col"
         data-testid="bm-3d-viewer">
      {/* ── Toolbar ────────────────────────────────────────────── */}
      <div className="bg-slate-950 border-b border-slate-800 flex items-center gap-3 px-4 py-2.5 shadow-sm">
        <Cube size={20} weight="duotone" className="text-cyan-400" />
        <div className="flex-1">
          <h2 className="text-[13px] font-bold text-white leading-tight">
            Interactive 3D Block Model
          </h2>
          <div className="text-[10.5px] text-slate-400 font-mono">
            {status === "loading" && "Loading PLY point cloud…"}
            {status === "ready" && meta && `${meta.nPoints.toLocaleString()} points · scale ×${meta.scale.toFixed(3)}`}
            {status === "error" && "Failed to load — check that the block model has been estimated"}
            {status === "idle" && "Waiting for project…"}
          </div>
        </div>

        {/* Colour-by toggles */}
        <div className="flex items-center gap-1 border border-slate-700 rounded px-1 py-0.5"
             data-testid="bm-3d-colour-by">
          <Palette size={12} className="text-cyan-400" />
          {["grade", "classification", "ore_waste"].map((mode) => (
            <button key={mode} onClick={() => setColourBy(mode)}
                    data-testid={`bm-3d-colour-${mode}`}
                    className={`h-6 px-2 text-[10.5px] font-semibold rounded transition ${
                      colourBy === mode
                        ? "bg-cyan-500 text-white"
                        : "text-slate-300 hover:bg-slate-800"
                    }`}>
              {mode.replace("_", "/")}
            </button>
          ))}
        </div>

        {/* Point-size slider */}
        <div className="flex items-center gap-2 text-[10.5px] text-slate-400">
          <span className="uppercase font-mono">pt&nbsp;size</span>
          <input type="range" min="0.8" max="6" step="0.2"
                  value={pointSize}
                  onChange={(e) => setPointSize(parseFloat(e.target.value))}
                  className="w-24 accent-cyan-400"
                  data-testid="bm-3d-point-size" />
          <span className="text-white font-mono w-6 text-right">{pointSize.toFixed(1)}</span>
        </div>

        {/* Max-points selector */}
        <select value={maxPoints}
                 onChange={(e) => setMaxPoints(parseInt(e.target.value, 10))}
                 className="h-6 text-[10.5px] bg-slate-800 text-slate-200 border border-slate-700 rounded px-1"
                 data-testid="bm-3d-max-points">
          <option value={20_000}>20 k pts · fast</option>
          <option value={60_000}>60 k pts · balanced</option>
          <option value={150_000}>150 k pts · detailed</option>
          <option value={400_000}>400 k pts · maximum</option>
        </select>

        <button onClick={() => setResetKey((k) => k + 1)}
                title="Reset camera"
                className="h-7 w-7 flex items-center justify-center rounded border border-slate-700 text-slate-300 hover:bg-slate-800"
                data-testid="bm-3d-reset-camera">
          <MagnifyingGlass size={12} />
        </button>
        <button onClick={() => setRefreshTick((t) => t + 1)}
                title="Reload data"
                className="h-7 w-7 flex items-center justify-center rounded border border-slate-700 text-slate-300 hover:bg-slate-800"
                data-testid="bm-3d-reload">
          <ArrowClockwise size={12} />
        </button>
        <button onClick={handleCapture}
                disabled={captureBusy || status !== "ready"}
                title="Capture current view → embed as CPR evidence"
                className={`h-7 px-2.5 flex items-center gap-1.5 rounded border text-[10.5px] font-semibold transition ${
                  captureBusy || status !== "ready"
                    ? "border-slate-700 text-slate-500 cursor-not-allowed"
                    : "border-emerald-500/70 text-emerald-300 hover:bg-emerald-500/15"
                }`}
                data-testid="bm-viewer-capture-btn">
          <Camera size={12} weight={captureBusy ? "regular" : "duotone"} />
          {captureBusy ? "Saving…" : "Capture View"}
        </button>
        <button onClick={() => setGalleryOpen((o) => !o)}
                title="Captured views gallery"
                className={`h-7 px-2 flex items-center gap-1.5 rounded border text-[10.5px] font-semibold transition ${
                  galleryOpen
                    ? "border-cyan-500 bg-cyan-500/15 text-cyan-100"
                    : "border-slate-700 text-slate-300 hover:bg-slate-800"
                }`}
                data-testid="bm-viewer-gallery-toggle">
          <Images size={12} />
          Gallery
          <span className="ml-1 px-1 rounded bg-slate-800 text-slate-300 text-[9.5px] font-mono"
                data-testid="bm-viewer-gallery-count">
            {gallery.length}
          </span>
        </button>
        {onClose && (
          <Button variant="outline" size="sm" onClick={onClose}
                  className="h-7 text-[11px] border-slate-700 text-slate-200 hover:bg-slate-800"
                  data-testid="bm-3d-close">
            <X size={11} className="mr-1" /> Close
          </Button>
        )}
      </div>

      {/* ── 3D Canvas ─────────────────────────────────────────── */}
      <div className="flex-1 relative">
        {status !== "ready" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-10">
            {status === "loading" ? (
              <>
                <ArrowsClockwise size={44} className="text-cyan-400 animate-spin" />
                <div className="mt-3 text-[13px] text-slate-300 font-mono">
                  Loading block model…
                </div>
              </>
            ) : status === "error" ? (
              <div className="text-[13px] text-rose-300 font-mono max-w-lg text-center">
                Could not load the block model. Either the project has no
                estimated model yet, or the PLY export failed.
              </div>
            ) : null}
          </div>
        )}
        <Canvas
          camera={{ position: [250, 200, 250], fov: 45, near: 0.1, far: 4000 }}
          gl={{ antialias: true, powerPreference: "high-performance",
                 alpha: false, preserveDrawingBuffer: true }}
          onCreated={({ gl }) => { gl.setClearColor("#0B1220"); glRef.current = gl; }}
          data-testid="bm-3d-canvas">
          <Suspense fallback={null}>
            <ambientLight intensity={0.6} />
            <directionalLight position={[500, 500, 500]} intensity={0.7} />
            {geometry && <BlockPointCloud geometry={geometry} pointSize={pointSize} />}
            <Grid args={[400, 400]} cellSize={20} cellThickness={0.5}
                   sectionSize={100} sectionThickness={1}
                   sectionColor="#334155" cellColor="#1E293B"
                   fadeDistance={800} infiniteGrid={false} />
            <OrbitControls
              makeDefault
              enableDamping dampingFactor={0.08}
              minDistance={1} maxDistance={5000}
              rotateSpeed={0.9} panSpeed={0.8} zoomSpeed={0.9} />
            <GizmoHelper alignment="bottom-right" margin={[70, 70]}>
              <GizmoViewport axisColors={["#F43F5E", "#10B981", "#2A5FFF"]}
                              labelColor="white" />
            </GizmoHelper>
            <ResetCameraButton resetKey={resetKey} geometry={geometry} />
            <CameraStateProbe stateRef={cameraStateRef} />
          </Suspense>
        </Canvas>

        {/* Legend overlay */}
        <div className="absolute top-3 left-3 bg-slate-950/85 border border-slate-800 rounded px-3 py-2 text-[10px] font-mono text-slate-200 pointer-events-none"
             data-testid="bm-3d-legend">
          <div className="text-cyan-400 font-bold mb-1 tracking-wider">LEGEND</div>
          {colourBy === "grade" && (
            <>
              <div>colour = grade (turbo ramp)</div>
              <div className="mt-1 flex items-center gap-1">
                <span className="inline-block w-16 h-2 rounded"
                       style={{ background: "linear-gradient(to right, #1E3A8A, #22D3EE, #FDE047, #F43F5E)" }} />
                <span className="ml-2 text-slate-400">low → high</span>
              </div>
            </>
          )}
          {colourBy === "classification" && (
            <>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-emerald-700" />Measured</div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-amber-500" />Indicated</div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-rose-700" />Inferred</div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-slate-500" />Unclassified</div>
            </>
          )}
          {colourBy === "ore_waste" && (
            <>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-emerald-500" />Ore  (≥ cut-off)</div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-slate-500" />Waste  (&lt; cut-off)</div>
            </>
          )}
          <div className="text-slate-500 mt-1">drag = rotate · scroll = zoom · shift-drag = pan</div>
        </div>

        {/* ── Captured Views Gallery drawer ─────────────────────────── */}
        {galleryOpen && (
          <div className="absolute top-3 right-3 bottom-3 w-[320px] bg-slate-950/95 border border-slate-800 rounded-lg shadow-2xl flex flex-col overflow-hidden"
               data-testid="bm-viewer-gallery-drawer">
            <div className="px-3 py-2 border-b border-slate-800 flex items-center gap-2">
              <Images size={14} className="text-cyan-400" />
              <div className="flex-1">
                <div className="text-[11.5px] font-bold text-white leading-tight">
                  Captured Views · CPR Evidence
                </div>
                <div className="text-[9.5px] text-slate-400 font-mono">
                  {gallery.length === 0
                    ? "No captures yet — hit Capture View above"
                    : `${gallery.length} view(s) · will embed in Chapter 19`}
                </div>
              </div>
              <button onClick={() => setGalleryOpen(false)}
                      className="h-6 w-6 flex items-center justify-center rounded hover:bg-slate-800 text-slate-400"
                      title="Close gallery"
                      data-testid="bm-viewer-gallery-close">
                <X size={12} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-2">
              {galleryLoading && (
                <div className="text-center text-slate-400 text-[10.5px] py-6">
                  Loading…
                </div>
              )}
              {!galleryLoading && gallery.length === 0 && (
                <div className="text-center text-slate-500 text-[10.5px] py-6 px-3">
                  Compose the view you want in the CPR, then click
                  <span className="text-emerald-300 font-semibold"> Capture View </span>
                  to save it as a report figure.
                </div>
              )}
              {gallery.map((v, i) => (
                <div key={v.id}
                     className="border border-slate-800 rounded-md overflow-hidden hover:border-cyan-500/70 transition"
                     data-testid={`bm-viewer-gallery-item-${i}`}>
                  <div className="relative aspect-[16/10] bg-slate-800">
                    <img src={`${API_BASE}/projects/${projectId}/block-model/captured-view/${v.id}/thumb.jpg`}
                         alt={v.caption || "captured view"}
                         className="w-full h-full object-cover"
                         data-testid={`bm-viewer-gallery-thumb-${i}`} />
                    <div className="absolute top-1 left-1 bg-slate-950/85 px-1.5 py-0.5 rounded text-[9px] font-mono text-cyan-300">
                      {(v.colour_mode || "grade").toUpperCase()}
                    </div>
                    {v.approved !== false && (
                      <div className="absolute top-1 right-1 bg-emerald-600/90 px-1 py-0.5 rounded text-[9px] font-mono text-white flex items-center gap-0.5"
                           data-testid={`bm-viewer-gallery-approved-${i}`}>
                        <CheckCircle size={10} weight="fill" /> CPR
                      </div>
                    )}
                  </div>
                  <div className="px-2 py-1.5 text-[10px] text-slate-200">
                    <div className="line-clamp-2 leading-tight">
                      {v.caption || "(no caption)"}
                    </div>
                    <div className="text-slate-500 mt-0.5 font-mono">
                      {v.created_at ? v.created_at.slice(0, 16).replace("T", " ") : "—"}
                      {" · "}
                      {v.n_points || 0} pts
                    </div>
                  </div>
                  <div className="flex items-center gap-1 px-2 pb-2">
                    <button onClick={() => handleEditCaption(v.id, v.caption)}
                            className="flex-1 h-6 text-[9.5px] font-semibold border border-slate-700 rounded text-slate-300 hover:bg-slate-800"
                            data-testid={`bm-viewer-gallery-edit-${i}`}>
                      Edit caption
                    </button>
                    <button onClick={() => handleDelete(v.id)}
                            className="h-6 w-6 flex items-center justify-center border border-rose-800 rounded text-rose-400 hover:bg-rose-950"
                            title="Delete"
                            data-testid={`bm-viewer-gallery-delete-${i}`}>
                      <Trash size={10} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
